import { i18n } from 'boot/i18n';
import { computed, ref } from 'vue';

import { useCohortAutoAssignAnalytics } from './useCohortAutoAssignAnalytics';

// eslint-disable-next-line import/prefer-default-export
export function useReviewAssignCohortsDialog({
  cohorts,
  reviewAssignments,
  surface,
}) {
  const {
    suggestedAssignments,
    manualAssignments,
    prepareReview,
    toggleAllSuggested,
    toggleSuggestedRow,
    updateManualSelection,
    collectAssignments,
  } = reviewAssignments;

  const {
    trackStepOneViewed,
    trackStepOneConfirmed,
    trackStepTwoViewed,
    trackStepTwoCompleted: trackStepTwoCompletedEvent,
    trackCancelled,
  } = useCohortAutoAssignAnalytics(surface);

  const STEPS = {
    SUGGESTED: 'suggested',
    MANUAL: 'manual',
  };

  const step = ref(STEPS.SUGGESTED);

  const cohortMap = computed(() => (
    cohorts.value.reduce((acc, cohort) => {
      acc[cohort.id] = cohort;
      return acc;
    }, {})
  ));

  const cohortOptions = computed(() => (
    cohorts.value.map((cohort) => ({
      label: cohort.cohortName,
      value: cohort.id,
    }))
  ));

  const hasSuggestedAssignments = computed(() => (
    suggestedAssignments.value.length > 0
  ));

  const hasManualAssignments = computed(() => (
    manualAssignments.value.length > 0
  ));

  const selectedSuggestedCount = computed(() => (
    suggestedAssignments.value.filter((item) => item.selected).length
  ));

  const selectedManualCount = computed(() => (
    manualAssignments.value.filter((item) => item.selectedCohortId).length
  ));

  const suggestedStepButtonLabel = computed(() => {
    if (hasManualAssignments.value) {
      return i18n.t('common.buttonDirectionNext');
    }

    return i18n.t('settings.careModel.cohorts.review.assignButton');
  });

  const canGoBack = computed(() => (
    step.value === STEPS.MANUAL && hasSuggestedAssignments.value
  ));

  const canGoNext = computed(() => {
    if (step.value !== STEPS.SUGGESTED) {
      return false;
    }

    return hasManualAssignments.value || selectedSuggestedCount.value > 0;
  });

  const canAssign = computed(() => {
    if (step.value === STEPS.SUGGESTED) {
      return selectedSuggestedCount.value > 0;
    }

    return selectedSuggestedCount.value > 0
      || selectedManualCount.value > 0;
  });

  function getCurrentCohortId(item) {
    return item.currentCohortId
      || item.client?.careModelCohort
      || null;
  }

  function getCohortName(cohortId) {
    if (!cohortId) {
      return '—';
    }

    return cohortMap.value[cohortId]?.cohortName || '—';
  }

  function getSuggestedCohort(item) {
    return item.cohort || item.suggestedCohort || null;
  }

  function getSuggestedCohortId(item) {
    return getSuggestedCohort(item)?.id
      || item.selectedCohortId
      || null;
  }

  const suggestedReviewRows = computed(() => (
    suggestedAssignments.value
      .map((item) => {
        const currentCohortId = getCurrentCohortId(item);
        const suggestedCohort = getSuggestedCohort(item);

        return {
          ...item,
          currentCohortId,
          currentCohortName: getCohortName(currentCohortId),
          suggestedCohort,
          suggestedCohortName: suggestedCohort?.cohortName || '—',
          selectedCohortId: getSuggestedCohortId(item),
          matchedLabels: item.matchedLabels || [],
          selected: item.selected ?? true,
        };
      })
      .sort((first, second) => (
        first.currentCohortName.localeCompare(second.currentCohortName)
      ))
  ));

  function initializeReview({ clients, columns }) {
    const review = prepareReview({
      clients,
      cohorts: cohorts.value,
      columns,
    });

    if (review.suggestedAssignments.length > 0) {
      trackStepOneViewed(review.suggestedAssignments.length);
    }

    if (
      review.suggestedAssignments.length === 0
      && review.manualAssignments.length > 0
    ) {
      step.value = STEPS.MANUAL;
      trackStepTwoViewed(review.manualAssignments.length, 'direct');
      return review;
    }

    step.value = STEPS.SUGGESTED;

    return review;
  }

  /**
   * Move from step one to step two.
   *
   * @returns {boolean} Whether the flow can be assigned immediately.
   */
  function goToNextStep() {
    trackStepOneConfirmed(
      selectedSuggestedCount.value,
      suggestedAssignments.value.length - selectedSuggestedCount.value,
    );

    if (hasManualAssignments.value) {
      step.value = STEPS.MANUAL;
      trackStepTwoViewed(
        manualAssignments.value.length,
        'from_step_one',
      );

      return false;
    }

    return true;
  }

  function goToPreviousStep() {
    step.value = STEPS.SUGGESTED;

    if (hasSuggestedAssignments.value) {
      trackStepOneViewed(suggestedAssignments.value.length);
    }
  }

  function trackStepTwoCompleted() {
    trackStepTwoCompletedEvent(
      selectedManualCount.value,
      manualAssignments.value.length - selectedManualCount.value,
    );
  }

  function trackDialogCancelled() {
    trackCancelled(
      step.value === STEPS.SUGGESTED ? 'step_one' : 'step_two',
    );
  }

  return {
    STEPS,
    step,
    suggestedAssignments,
    manualAssignments,
    cohortOptions,
    suggestedReviewRows,
    hasSuggestedAssignments,
    hasManualAssignments,
    selectedSuggestedCount,
    selectedManualCount,
    suggestedStepButtonLabel,
    canGoBack,
    canGoNext,
    canAssign,
    initializeReview,
    goToNextStep,
    goToPreviousStep,
    trackStepTwoCompleted,
    trackDialogCancelled,
    toggleAllSuggested,
    toggleSuggestedRow,
    updateManualSelection,
    collectAssignments,
  };
}
